import { Controller } from "@hotwired/stimulus"

// The Publishing desk: filter chips over the candidate list, and native HTML5
// drag from a candidate card onto the Typefully queue pane. A drop POSTs the
// SAME audited resource action the card's button uses (guards re-checked
// server-side), then reloads the desk so both panes reflect the new state.
export default class extends Controller {
  static targets = ["candidate", "chip", "dropzone", "check", "bulkDismiss", "post", "unfurlAll", "selectAll"]

  connect() {
    this.dragging = null
    this.postDrag = null
    if (this.hasDropzoneTarget) {
      const zone = this.dropzoneTarget
      zone.addEventListener("dragover", (e) => {
        if (!this.dragging) return
        e.preventDefault()
        e.dataTransfer.dropEffect = "copy"
        zone.classList.add("ring-2", "ring-violet-500")
      })
      zone.addEventListener("dragleave", () => zone.classList.remove("ring-2", "ring-violet-500"))
      zone.addEventListener("drop", (e) => {
        e.preventDefault()
        zone.classList.remove("ring-2", "ring-violet-500")
        this.queueDragged()
      })
    }
  }

  filter(event) {
    const kind = event.currentTarget.dataset.kind
    this.chipTargets.forEach((c) => c.setAttribute("aria-pressed", c.dataset.kind === kind ? "true" : "false"))
    this.candidateTargets.forEach((card) => {
      card.hidden = kind !== "all" && card.dataset.kind !== kind
    })
    this.syncSelectAll()
  }

  // ── queue post collapse/unfurl ─────────────────────────────────────────

  togglePost(event) {
    if (event.target.closest("a, button, form, input, textarea")) return // header actions don't toggle
    this.setPostOpen(event.currentTarget.closest("[data-publish-desk-target='post']"), null)
  }

  unfurlAll() {
    const open = this.unfurlAllTarget.getAttribute("aria-pressed") !== "true"
    this.unfurlAllTarget.setAttribute("aria-pressed", open ? "true" : "false")
    this.unfurlAllTarget.textContent = open ? "Collapse all" : "Unfurl all"
    this.postTargets.forEach((post) => this.setPostOpen(post, open))
  }

  // open: true/false forces, null toggles. Card rasters lazy-load on first open.
  setPostOpen(post, open) {
    const body = post.querySelector("[data-post-body]")
    if (!body) return
    const show = open === null ? body.hidden : open
    body.hidden = !show
    const chevron = post.querySelector("[data-post-chevron]")
    if (chevron) chevron.style.transform = show ? "rotate(90deg)" : ""
    if (show) {
      const img = body.querySelector("img[data-card-src]")
      if (img && !img.src) img.src = img.dataset.cardSrc
    }
  }

  checked() {
    const n = this.checkTargets.filter((c) => c.checked).length
    this.bulkDismissTarget.hidden = n === 0
    this.bulkDismissTarget.textContent = `Dismiss selected (${n})`
    this.syncSelectAll()
  }

  // Check every VISIBLE dismissable candidate (the filter chips hide cards, and a
  // hidden card shouldn't be swept into a bulk dismiss it can't be seen in); if all
  // visible ones are already checked, clear them instead.
  selectAll() {
    const boxes = this.visibleChecks()
    const allChecked = boxes.length > 0 && boxes.every((c) => c.checked)
    boxes.forEach((c) => { c.checked = !allChecked })
    this.checked()
  }

  syncSelectAll() {
    if (!this.hasSelectAllTarget) return
    const boxes = this.visibleChecks()
    const checked = boxes.filter((c) => c.checked).length
    this.selectAllTarget.checked = boxes.length > 0 && checked === boxes.length
    this.selectAllTarget.indeterminate = checked > 0 && checked < boxes.length
  }

  visibleChecks() {
    return this.checkTargets.filter((c) => !c.closest("[data-publish-desk-target='candidate']")?.hidden)
  }

  // Dismiss every checked candidate: snaps skip, published stories mark shared.
  // One POST per resource/action group — the same audited actions as the buttons.
  async bulkDismiss() {
    const checked = this.checkTargets.filter((c) => c.checked)
    if (!checked.length) return
    if (!confirm(`Dismiss ${checked.length} selected (snaps skip, stories mark shared)?`)) return

    const groups = new Map()
    checked.forEach((c) => {
      const key = `${c.dataset.resource}/${c.dataset.dismissAction}`
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(c.dataset.recordId)
    })
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    for (const [key, ids] of groups) {
      const body = new FormData()
      ids.forEach((id) => body.append("ids[]", id))
      await fetch(`/admin/resources/${key.split("/")[0]}/action/${key.split("/")[1]}`, {
        method: "POST", headers: {"X-CSRF-Token": csrf}, body, redirect: "follow"
      }).catch(() => {})
    }
    window.Turbo ? window.Turbo.visit(window.location.href, {action: "replace"}) : window.location.reload()
  }

  dragstart(event) {
    const card = event.currentTarget
    this.dragging = {resource: card.dataset.resource, actionKey: card.dataset.actionKey, id: card.dataset.recordId}
    event.dataTransfer.effectAllowed = "copy"
    card.classList.add("opacity-50")
  }

  dragend(event) {
    event.currentTarget.classList.remove("opacity-50")
    // Cleared on the next tick so the drop handler still sees it.
    setTimeout(() => { this.dragging = null }, 50)
  }

  // ── reorder the queue by dragging a post between rows ───────────────────
  //
  // Kept separate from `this.dragging` (the candidate→queue drag) so the two never
  // cross: the queue dropzone's handlers bail when `this.dragging` is null, which is
  // exactly the case while a post is being dragged.
  //
  // The drop position comes from GEOMETRY at the list level, not from which row
  // happened to receive the last dragover: WebKit only reliably honors a drop when
  // dragenter is cancelled as well as dragover, and per-row targeting left the row
  // seams and container padding dead. The list container spans all of it, and the
  // insertion point is the count of rows whose midpoint sits above the cursor —
  // the drop-between-two-posts semantics the drag hand expects.

  postDragStart(event) {
    const row = event.currentTarget
    // Don't start a drag from inside the expanded body — the textarea needs to select.
    if (event.target.closest("[data-post-body], button, a, input")) {
      event.preventDefault()
      return
    }
    this.postDrag = {id: row.dataset.draftId, index: Number(row.dataset.postIndex)}
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", row.dataset.draftId) // Firefox needs a payload
    row.classList.add("opacity-40")
  }

  postDragEnd(event) {
    event.currentTarget.classList.remove("opacity-40")
    this.clearDropHints()
    setTimeout(() => { this.postDrag = null }, 50)
  }

  queueDragEnter(event) {
    if (!this.postDrag) return
    event.preventDefault() // WebKit needs dragenter cancelled before it honors the drop
  }

  queueDragOver(event) {
    if (!this.postDrag) return
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    this.paintDropHint(this.insertionIndex(event.clientY))
  }

  queueDragLeave(event) {
    // Crossing into a child fires dragleave too — only clear on truly leaving the list.
    if (event.currentTarget.contains(event.relatedTarget)) return
    this.clearDropHints()
  }

  async queueDrop(event) {
    if (!this.postDrag) return
    event.preventDefault()
    event.stopPropagation() // never also fire the candidate dropzone's handler
    const {id, index} = this.postDrag
    const insertion = this.insertionIndex(event.clientY)
    this.postDrag = null
    this.clearDropHints()
    // Insertion point → destination index: rows above the gap keep their index;
    // removing the dragged row first shifts the downward case by one.
    const position = insertion > index ? insertion - 1 : insertion
    if (position === index) return

    const body = new FormData()
    body.append("position", position)
    body.append("_method", "patch")
    try {
      await fetch(`/admin/typefully/drafts/${id}/move`, {
        method: "POST",
        headers: {"X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content},
        body,
        redirect: "follow"
      })
    } finally {
      // Reload either way: the server is the authority on the resulting order, and a
      // failure needs to render its flash rather than leave the row visually moved.
      window.Turbo ? window.Turbo.visit(window.location.href, {action: "replace"}) : window.location.reload()
    }
  }

  // 0..N: how many rows sit (by vertical midpoint) above the cursor.
  insertionIndex(clientY) {
    return this.postTargets.filter((row) => {
      const rect = row.getBoundingClientRect()
      return rect.top + rect.height / 2 < clientY
    }).length
  }

  paintDropHint(insertion) {
    this.clearDropHints()
    const rows = this.postTargets
    if (!rows.length) return
    if (insertion < rows.length) rows[insertion].classList.add("border-t-2", "border-violet-500")
    else rows[rows.length - 1].classList.add("border-b-2", "border-violet-500")
  }

  clearDropHints() {
    this.postTargets.forEach((p) => p.classList.remove("border-t-2", "border-b-2", "border-violet-500"))
  }

  async queueDragged() {
    const drag = this.dragging
    if (!drag) return
    this.dragging = null
    const body = new FormData()
    body.append("ids[]", drag.id)
    try {
      await fetch(`/admin/resources/${drag.resource}/action/${drag.actionKey}`, {
        method: "POST",
        headers: {"X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content},
        body,
        redirect: "follow"
      })
    } finally {
      window.Turbo ? window.Turbo.visit(window.location.href, {action: "replace"}) : window.location.reload()
    }
  }
}
